import { useCallback, useEffect, useState } from 'react';

import { googleTagManagerConfig } from '../../../config/googleTagManagerConfig';
import {
  getCurrentEnvironment,
  isProdEnvironment,
} from '../../../interfaces/environment/Environment';
import { useLocalisation } from '../../../services/localisationContext';
import { getWindow } from '../../../services/windowService';
import { useApp } from '../../useApp';
import { gtagQueue } from '../useGoogleAnalytics/gtagQueue';
import { isGTMScriptLoaded } from './isGTMScriptLoaded';

const wnd = getWindow();

export const useGoogleTagManager = (activated: boolean) => {
  /* app hooks */
  const { currentLocale } = useLocalisation();
  const { loggedIn } = useApp();

  /* local states */
  const [loaded, setLoaded] = useState<boolean>(isGTMScriptLoaded.get());

  /* dispose */
  useEffect(() => {
    return isGTMScriptLoaded.subscribe(setLoaded);
  }, []);

  /* set user properties */
  useEffect(() => {
    if (!activated || !loaded || currentLocale?.length === 2) {
      return;
    }
    const { gtag } = wnd;
    gtag('set', 'user_properties', {
      ui_locale: currentLocale,
      ui_logged_in: loggedIn,
    });
  }, [currentLocale, loggedIn, loaded, activated]);

  /* exported function to send pageView */
  const gaPageView = useCallback(
    (title: string, userProperties?: Record<string, string>) => {
      if (!activated || !loaded) {
        return;
      }
      const payload = {
        pageTitle: title,
        pageLocation: wnd.location.href,
        pagePath: wnd.location.pathname,
        sendTo: googleTagManagerConfig.gtmMeasurementId,
        userProperties: userProperties,
        environment: getGoogleTagManagerEnvironment(),
      };
      gtagQueue.set([['event', 'page_view', payload]]);
    },
    [activated, loaded],
  );

  const gaRequestConfiguration = useCallback(
    (payload: RequestFormPayload) => {
      if (!activated || !loaded) {
        return;
      }
      gtagQueue.set([['event', 'request_form', payload]]);
    },
    [activated, loaded],
  );

  const gaLoginEvent = useCallback(() => {
    gtagQueue.set([['event', 'login']]);
  }, []);

  const gaLogoutEvent = useCallback(() => {
    gtagQueue.set([['event', 'logout']]);
  }, []);

  const gaCreateConfigurationEvent = useCallback((options: CreateConfigurationPayload) => {
    const { articleNumber, templateName } = options;
    if (articleNumber) {
      gtagQueue.set([['event', 'create_configuration', { article_number: articleNumber }]]);
    }
    if (templateName) {
      gtagQueue.set([['event', 'create_configuration', { template_name: templateName }]]);
    }
  }, []);

  const getGoogleTagManagerEnvironment = (): string | undefined => {
    if (isProdEnvironment()) {
      return undefined;
    } else if (!getCurrentEnvironment()) {
      return 'dev';
    } else {
      return getCurrentEnvironment().toLowerCase();
    }
  };

  return {
    gaPageView,
    gaRequestConfiguration,
    gaLoginEvent,
    gaLogoutEvent,
    gaCreateConfigurationEvent,
  };
};

export type RequestFormPayload = {
  event_category: 'Submit' | 'Open';
  event_label: 'anonymous_user' | 'signed_in_user';
} & Record<string, any>;

type CreateConfigurationPayload = { articleNumber?: string; templateName?: string };
